import React, { useState, useEffect } from 'react';
import { Users, Calendar, Search, X, ArrowLeft, Sparkles, Play } from 'lucide-react';
import { api, Venue } from '../services/api';
import ProWelcomeTour from './ProWelcomeTour';
import AvailabilityTable from './AvailabilityTable';
import RestaurantAvailabilityCards from './RestaurantAvailabilityCards';
import VenueAlertButton from './VenueAlertButton';
import { Link, useSearchParams } from 'react-router-dom';
import { UserProfile } from '../services/api';
import { uniqueBookableSlots } from '../utils/availability';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import '../styles/custom-datepicker.css';
import '../styles/availability-mobile.css';


export interface AvailabilitySlot {
  venue_id: number;
  slot_type?: string;
  restaurant: string;
  date: string;
  party_size: number;
  available_time: string;
  booking_url: string;
  image: string | null;
  proxied_image?: string | null;
}

interface FilterState {
  restaurant: string;
  date: string;
  partySize: string;
  visitStatus: string;
}

interface OpenTableAvailabilityProps {
  userProfile: UserProfile | null;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>;
  previewData?: { slots: AvailabilitySlot[]; restaurants: Venue[] };
  onTourActiveChange?: (active: boolean) => void;
}

const OpenTableAvailability: React.FC<OpenTableAvailabilityProps> = ({ userProfile, setUserProfile, previewData, onTourActiveChange }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tourOpen, setTourOpen] = useState(() => searchParams.get('welcome') === '1' || !!previewData);
  const finishTour = () => {
    setTourOpen(false);
    setSearchParams(previous => { const next = new URLSearchParams(previous); next.delete('welcome'); return next; }, { replace: true });
    window.scrollTo({ top: 0, behavior: 'auto' });
  };
  useEffect(() => {
    onTourActiveChange?.(tourOpen);
    return () => onTourActiveChange?.(false);
  }, [tourOpen, onTourActiveChange]);
  const showingAlerts = searchParams.get('view') === 'alerts';
  const [restaurants, setRestaurants] = useState<Venue[]>([]);
  const [allAvailability, setAllAvailability] = useState<AvailabilitySlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availabilityLayout, setAvailabilityLayout] = useState<'table' | 'restaurants'>('table');
  const [filters, setFilters] = useState<FilterState>({
    restaurant: searchParams.get('restaurant') || '',
    date: '',
    partySize: '',
    visitStatus: ''
  });

  const availableDates = Array.from(new Set(allAvailability.map(slot => slot.date)));

  useEffect(() => {
    if (previewData) {
      setRestaurants(previewData.restaurants);
      setAllAvailability(uniqueBookableSlots(previewData.slots));
      setLoading(false);
      return;
    }
    const fetchAvailability = async () => {
      try {
        setLoading(true);
        const [response, catalogue] = await Promise.all([
          api.get<AvailabilitySlot[]>('/availability/'),
          api.get<Venue[]>('/availability/restaurants/'),
        ]);
        if (!Array.isArray(catalogue.data)) throw new Error('Invalid restaurant catalogue');
        setRestaurants(catalogue.data);

        if (!Array.isArray(response.data)) {
          setError('Invalid response format');
          return;
        }

        const uniqueSlots = uniqueBookableSlots(response.data);

        setAllAvailability(uniqueSlots);
        setError(null);
      } catch (err) {
        setError('Failed to load availability data');
        console.error('Error fetching availability:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailability();
  }, [previewData]);

  const availableVenueIds = new Set(allAvailability.map(slot => slot.venue_id));
  const visitedVenueIds = new Set((userProfile?.visited_venues || []).map(venue => venue.id));
  const awaitingRestaurants = restaurants.filter(venue => !availableVenueIds.has(venue.id));
  const matchingAwaiting = awaitingRestaurants.filter(venue =>
    venue.name.toLowerCase().includes(filters.restaurant.trim().toLowerCase()));
  const changeView = (view: 'available' | 'alerts') => {
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      if (view === 'alerts') next.set('view', 'alerts'); else next.delete('view');
      return next;
    });
  };

  const filteredAvailability = allAvailability.filter(slot => {
    const matchesRestaurant = !filters.restaurant ||
      slot.restaurant.toLowerCase().includes(filters.restaurant.trim().toLowerCase());
    const matchesDate = !filters.date || slot.date === filters.date;
    const matchesPartySize = !filters.partySize ||
      slot.party_size === parseInt(filters.partySize);
    const matchesVisitStatus = filters.visitStatus !== 'not-visited' ||
      !visitedVenueIds.has(slot.venue_id);

    return matchesRestaurant && matchesDate && matchesPartySize && matchesVisitStatus;
  });

  const clearFilters = () => {
    setFilters({
      restaurant: '',
      date: '',
      partySize: '',
      visitStatus: ''
    });
  };

  const anyFiltersActive = Object.values(filters).some(value => value !== '');

  // date.toISOString() converts to UTC first. For anyone west of UTC that's
  // still fine, but during British Summer Time local midnight is 23:00 UTC
  // the day before, so "today" silently asked the API for yesterday - every
  // UK visitor picking "today" got zero results for as long as BST is in
  // effect. Format from the local fields instead.
  const toLocalDateStr = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Function to check if a date is available
  const isDateAvailable = (date: Date): boolean => {
    return availableDates.includes(toLocalDateStr(date));
  };

  // Function to check if a date is within next 7 days
  const isWithinNext7Days = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sevenDaysFromNow = new Date(today);
    sevenDaysFromNow.setDate(today.getDate() + 7);
    return date >= today && date <= sevenDaysFromNow;
  };

  // Custom date selection handler
  const handleDateChange = (date: Date | null) => {
    if (date) {
      setFilters(prev => ({ ...prev, date: toLocalDateStr(date) }));
    } else {
      setFilters(prev => ({ ...prev, date: '' }));
    }
  };

  // Same 8-day window the desktop calendar allows (minDate today, maxDate
  // +7), laid out as tappable chips instead of a month grid. A full grid is
  // mostly grey on a phone when only a week is ever selectable, and its day
  // cells are well under the ~44px touch target a thumb needs.
  const next7Days = Array.from({ length: 8 }, (_, i) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + i);
    return date;
  });

  const [showFixedHeader, setShowFixedHeader] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100 && !tourOpen) {
        // Show the fixed header after scrolling down ~100px
        setShowFixedHeader(true);
      } else {
        setShowFixedHeader(false);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tourOpen]);

  return (
    <div className="w-full availability-page">
      {tourOpen && !loading && <ProWelcomeTour onFinish={finishTour} preview={!!previewData} />}
      <div className={`bg-surface/95 backdrop-blur-sm fixed top-0 left-0 right-0 z-40 transition-all duration-300 shadow-sm border-b border-ink-200 ${showFixedHeader ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}>        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-14">
          <Link
            to="/"
            className="flex items-center text-ink-600 hover:text-ink-900"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>

          <div className="flex flex-col ml-4">
            <span className="text-sm font-medium text-ink-900">
              Table Availability
            </span>
            <span className="text-xs text-ink-500 -mt-0.5">
              Refreshed after each check
            </span>
          </div>

          {/* // Improved live indicator with fuzzier glow and slower animation */}
          <div className="ml-auto flex items-center text-xs text-ink-500">
            <div className="relative w-2 h-2 mr-2">
              {/* Core dot */}
              <div className="absolute inset-0 bg-green-500 rounded-full"></div>

              {/* 2-second ping animation */}
              <div className="absolute inset-0 bg-green-400 rounded-full animate-[ping_2s_cubic-bezier(0,0,.2,1)_infinite] opacity-70"></div>

              {/* Subtle glow - reduced shadow spread and opacity */}
              <div className="absolute inset-0 rounded-full shadow-[0_0_2px_1px_rgba(34,197,94,0.15)]"></div>
            </div>
            <Link to="/alerts" className="underline">Your alerts · {userProfile?.alert_venue_ids?.length ?? 0}/5</Link>
          </div>




        </div>
      </div>
      </div>

      {/* Add a spacer to prevent content from going under the fixed header */}
      {showFixedHeader && <div className="h-14" />}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pro-pass-bar">
          <Sparkles size={20} /><div className="flex flex-col"><strong>{previewData ? 'Eatsy Pro · Preview pass' : 'Eatsy Pro · Your access is active'}</strong><span>{previewData ? 'Sample restaurants and times · no payment needed' : 'Table availability & restaurant alerts, all yours'}</span></div>
          <button type="button" onClick={() => { clearFilters(); changeView('available'); setTourOpen(true); }}><Play size={13} /> Replay tour</button>
        </div>
        <div className="availability-heading mb-7 flex flex-wrap items-end justify-between gap-4">
          <div><p className="text-xs font-semibold uppercase tracking-widest text-clay mb-3">A table worth finding</p><h1 className="font-display text-4xl sm:text-5xl mb-3">Table availability</h1><p className="text-ink-600 max-w-xl">Book a table that's available, or let us watch a restaurant for you.</p></div>
          <Link data-pro-tour="manage" to={previewData ? "/dev/pro-welcome?view=alerts" : "/alerts"} className="rounded-full border border-brand-200 bg-brand-50 px-4 py-2.5 text-sm font-medium text-brand-800">Your alerts · {userProfile?.alert_venue_ids?.length ?? 0}/5</Link>
        </div>
        <div className="flex gap-2 mb-5" aria-label="Availability view">
          <button data-pro-tour="tables" type="button" onClick={() => changeView('available')} aria-pressed={!showingAlerts} className={`rounded-full px-4 py-3 text-sm font-medium ${!showingAlerts ? 'bg-brand-600 text-white' : 'border border-ink-200 bg-surface text-ink-600'}`}>Available tables{!loading && !error ? ` · ${availableVenueIds.size}` : ''}</button>
          <button data-pro-tour="watch" type="button" onClick={() => changeView('alerts')} aria-pressed={showingAlerts} className={`rounded-full px-4 py-3 text-sm font-medium ${showingAlerts ? 'bg-brand-600 text-white' : 'border border-ink-200 bg-surface text-ink-600'}`}>Alert me{!loading && !error ? ` · ${awaitingRestaurants.length}` : ''}</button>
        </div>
        {showingAlerts && <p className="mb-5 text-sm text-ink-600">No tables listed at these restaurants right now. Choose up to 5 and we'll email you when a scheduled check finds availability. Alerts cover all dates and party sizes we check.</p>}
        <div data-pro-tour="filters" className="availability-filters bg-surface rounded-lg shadow-sm border border-ink-200 p-4 sm:p-6 mb-6">
          <div className={`availability-filter-grid grid gap-4 ${showingAlerts ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'}`}>
            <div className={showingAlerts ? 'hidden' : 'space-y-1'}>
              <label className="block text-sm font-medium text-ink-700">
                Party Size
              </label>
              <div className="relative flex items-center">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 flex justify-center pointer-events-none">
                  <Users className="w-5 h-5 text-ink-400" />
                </div>
                <select
                  aria-label="Party size"
                  value={filters.partySize}
                  onChange={(e) => setFilters(prev => ({ ...prev, partySize: e.target.value }))}
                  className="w-full pl-10 pr-4 py-3 border border-ink-300 rounded-lg
                            focus:ring-2 focus:ring-brand-500 focus:border-brand-500
                            appearance-none mobile-select"
                >
                  <option value="">Any Size</option>
                  {Array.from(new Set(allAvailability.map(slot => slot.party_size)))
                    .sort((a, b) => a - b)
                    .map(size => (
                      <option key={size} value={size}>
                        {size} {size === 1 ? 'Person' : 'People'}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {/* Date Filter */}
            <div className={showingAlerts ? 'hidden' : 'space-y-1'}>
              <label className="block text-sm font-medium text-ink-700">
                Date
              </label>

              <select aria-label="Date" value={filters.date}
                onChange={event => setFilters(previous => ({ ...previous, date: event.target.value }))}
                className="sm:hidden w-full border border-ink-300 rounded-lg px-2 h-10 bg-surface">
                <option value="">All dates</option>
                {next7Days.filter(isDateAvailable).map(date => <option key={toLocalDateStr(date)} value={toLocalDateStr(date)}>
                  {date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                </option>)}
              </select>

              {/* Desktop: the full react-datepicker month view, with a mouse
                  and room for it, is the better fit. */}
              <div className="hidden sm:flex relative items-center">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 flex justify-center pointer-events-none z-10">
                  <Calendar className="w-5 h-5 text-ink-400" />
                </div>
                <DatePicker
                  selected={filters.date ? new Date(filters.date) : null}
                  onChange={handleDateChange}
                  dateFormat="EEEE, MMMM d"
                  placeholderText="Select date"
                  minDate={new Date()}
                  maxDate={new Date(new Date().setDate(new Date().getDate() + 7))}
                  filterDate={date => isWithinNext7Days(date) && isDateAvailable(date)}
                  className="w-full pl-10 pr-4 py-3 border border-ink-300 rounded-lg
                                focus:ring-2 focus:ring-brand-500 focus:border-brand-500
                                mobile-date-input"
                  showPopperArrow={false}
                  isClearable
                  dayClassName={date =>
                    isDateAvailable(date) ?
                      "cursor-pointer hover:bg-brand-50" :
                      "text-ink-300 cursor-not-allowed"
                  }
                  calendarClassName="bg-surface shadow-lg border border-ink-200 rounded-lg"
                />
              </div>
            </div>

            <div className={showingAlerts || !userProfile ? 'hidden' : 'space-y-1'}>
              <label className="block text-sm font-medium text-ink-700">
                Visit history
              </label>
              <select aria-label="Visit history" value={filters.visitStatus}
                onChange={event => setFilters(previous => ({ ...previous, visitStatus: event.target.value }))}
                className="w-full border border-ink-300 rounded-lg px-3 py-3 bg-surface focus:ring-2 focus:ring-brand-500 focus:border-brand-500">
                <option value="">All places</option>
                <option value="not-visited">Not visited</option>
              </select>
            </div>

            <div className={`space-y-1 ${!userProfile ? 'availability-search-wide' : ''}`}>
              <label className="block text-sm font-medium text-ink-700">
                Search
              </label>
              <div className="relative flex items-center">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 flex justify-center pointer-events-none">
                  <Search className="w-5 h-5 text-ink-400" />
                </div>
                <input
                  type="search"
                  aria-label="Search restaurants"
                  value={filters.restaurant}
                  onChange={(e) => setFilters(prev => ({ ...prev, restaurant: e.target.value }))}
                  placeholder="Restaurant name..."
                  className="w-full pl-10 pr-4 py-3 border border-ink-300 rounded-lg
                            focus:ring-2 focus:ring-brand-500 focus:border-brand-500
                            mobile-input"
                />
              </div>
            </div>
          </div>

          {anyFiltersActive && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={clearFilters}
                className="flex items-center px-3 py-2 text-sm text-ink-600 hover:text-ink-900"
              >
                <X className="w-4 h-4 mr-1" />
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {!loading && !error && !showingAlerts && filteredAvailability.length > 0 && <div className="availability-layout-toggle mb-4 flex items-center justify-between gap-3">
          <span className="text-sm text-ink-500">View availability</span>
          <div className="inline-flex rounded-lg border border-ink-200 bg-surface p-1" aria-label="Availability layout">
            <button type="button" aria-pressed={availabilityLayout === 'table'} onClick={() => setAvailabilityLayout('table')}
              className={`rounded-md px-3 py-2 text-sm ${availabilityLayout === 'table' ? 'bg-brand-600 text-white' : 'text-ink-600 hover:bg-ink-50'}`}>Time table</button>
            <button type="button" aria-pressed={availabilityLayout === 'restaurants'} onClick={() => setAvailabilityLayout('restaurants')}
              className={`rounded-md px-3 py-2 text-sm ${availabilityLayout === 'restaurants' ? 'bg-brand-600 text-white' : 'text-ink-600 hover:bg-ink-50'}`}>Restaurants</button>
          </div>
        </div>}

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-brand-500 border-t-transparent"></div>
            <p className="mt-2 text-ink-600">Loading availability...</p>
          </div>
        ) : error ? (
          <div className="bg-surface rounded-lg shadow-sm border border-ink-200 p-6 text-center">
            <p className="text-red-600">{error}</p>
          </div>
        ) : showingAlerts ? null : filteredAvailability.length === 0 ? (
          <div className="bg-surface rounded-lg shadow-sm border border-ink-200 p-6 text-center">
            <h2 className="font-display text-2xl mb-2">No tables match your search</h2>
            <p className="text-ink-600">{matchingAwaiting.length > 0 && filters.restaurant.trim() ? 'You can turn on an alert for a matching restaurant below.' : 'Try another restaurant, date or party size, or explore restaurants to watch.'}</p>
            <div className="mt-4 flex flex-wrap justify-center gap-4">{anyFiltersActive && <button onClick={clearFilters} className="text-sm text-brand-700 underline">Clear search and filters</button>}<button onClick={() => changeView('alerts')} className="text-sm text-brand-700 underline">Find restaurants to watch</button></div>
          </div>
        ) : (
          availabilityLayout === 'table'
            ? <AvailabilityTable key={JSON.stringify(filters)} slots={filteredAvailability} />
            : <RestaurantAvailabilityCards slots={filteredAvailability} />
        )}
        {!loading && !error && (showingAlerts || (filters.restaurant.trim() && matchingAwaiting.length > 0)) && (
          <section className="mt-6" aria-labelledby="waiting-restaurants">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 id="waiting-restaurants" className="font-display text-2xl">{showingAlerts ? 'Restaurants to watch' : 'Waiting for a table?'}</h2>
              <span className="text-sm text-ink-500">{matchingAwaiting.length} {matchingAwaiting.length === 1 ? 'restaurant' : 'restaurants'}</span>
            </div>
            {!showingAlerts && <p className="text-sm text-ink-600 mb-5">No tables are listed for these restaurants across the dates and party sizes we check. Get an email when availability appears.</p>}
            {(userProfile?.alert_venue_ids?.length ?? 0) >= 5 && <p className="rounded-xl bg-brand-50 p-4 mb-5 text-sm text-brand-800">You've selected 5 restaurants. <Link to="/alerts" className="underline">Manage your alerts</Link> to make room for another.</p>}
            {matchingAwaiting.length === 0 ? <p className="py-6 text-ink-600">{filters.restaurant.trim() ? 'No restaurants to watch match that name. Try Available tables or another search.' : 'All restaurants we check have tables listed. Explore Available tables to book.'}</p> : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {matchingAwaiting.map(venue => {
                  const isOn = userProfile?.alert_venue_ids?.includes(venue.id);
                  return <article key={venue.id} className="rounded-2xl border border-ink-200 bg-surface p-5">
                    <p className="text-xs text-ink-500 mb-2">{venue.city} · No tables listed</p>
                    <h3 className="font-display text-xl mb-3">{venue.name}</h3>
                    <p className="text-sm text-ink-600 mb-4 break-words">{isOn ? `We'll email ${userProfile?.user.email} when we find a table.` : "We'll keep an eye on availability for you."}</p>
                    {previewData ? <span className="inline-flex rounded-full border border-brand-200 px-4 py-2 text-sm text-brand-700">Alert me · preview only</span> : <VenueAlertButton venue={venue} userProfile={userProfile} setUserProfile={setUserProfile} />}
                  </article>;
                })}
              </div>
            )}
            <p className="text-xs text-ink-500 mt-5">Alerts cover all dates and party sizes we check, independently of your search filters. We won't email you the same sitting twice. Availability can change before you book.</p>
          </section>
        )}
      </div>
    </div>
  );
};

export default OpenTableAvailability;
